import {
  type EntityId,
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { deleteJSON, postJSON, putJSON } from 'api/fetch';
import { RootState } from 'initializers/types';
import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';

import Schedule from 'features/scheduleBuilder/ScheduleBuilderView/resources/Schedule';
import { selectDatesInCurrentRange } from 'features/scheduleBuilder/ScheduleBuilderView/selectors';

import { actions as globalToastActions } from 'components/GlobalToast/slice';

import { datesInRange } from 'util/dateTime';
import * as flashNotice from 'util/flashNotice';
import { toI18n } from 'util/i18n';

import { captureHydrationException } from '../helpers';
import type { ScheduleFetchAction } from '../types';

import { PARENT_SLICE_NAME, SLICE_NAME } from './constants';
import type {
  TimeOffBlackoutPeriod,
  TimeOffBlackoutPeriodSerializedEntity,
  TimeoffBlackoutPeriodsRequestPayload,
  TimeOffBlackoutPeriodsState,
} from './types';

const I18N_PATH = 'schedule_builder.react_page.time_off_blackout_form';
const API_PATH = '/schedule_builder/time_off_blackout_periods';

export const createTimeOffBlackout = createAsyncThunk(
  `${SLICE_NAME}/createTimeOffBlackout`,
  async (
    { payload }: { payload: TimeoffBlackoutPeriodsRequestPayload },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await postJSON(API_PATH, {
        data: {
          attributes: payload,
        },
      });

      dispatch(
        globalToastActions.openGlobalToast({
          content: toI18n(`${I18N_PATH}.create_success_message`),
          toastProps: { variant: 'success' },
          initiatedFrom: 'ScheduleBuilder',
        })
      );

      return response.data;
    } catch (error) {
      flashNotice.error(toI18n('errors.generic'));
      return rejectWithValue(error);
    }
  }
);

export const updateTimeOffBlackout = createAsyncThunk(
  `${SLICE_NAME}/updateTimeOffBlackout`,
  async (
    {
      id,
      payload,
    }: { id: string; payload: TimeoffBlackoutPeriodsRequestPayload },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await putJSON(`${API_PATH}/${id}`, {
        data: {
          attributes: payload,
        },
      });

      dispatch(
        globalToastActions.openGlobalToast({
          content: toI18n(`${I18N_PATH}.update_success_message`),
          toastProps: { variant: 'success' },
          initiatedFrom: 'ScheduleBuilder',
        })
      );

      return response.data;
    } catch (error) {
      flashNotice.error(toI18n('errors.generic'));
      return rejectWithValue(error);
    }
  }
);

export const deleteTimeOffBlackout = createAsyncThunk(
  `${SLICE_NAME}/deleteTimeOffBlackout`,
  async ({ id }: { id: string }, { rejectWithValue, dispatch }) => {
    try {
      await deleteJSON(`${API_PATH}/${id}`);

      dispatch(
        globalToastActions.openGlobalToast({
          content: toI18n(`${I18N_PATH}.delete_success_message`),
          toastProps: { variant: 'success' },
          initiatedFrom: 'ScheduleBuilder',
        })
      );

      return id;
    } catch (error) {
      flashNotice.error(toI18n('errors.generic'));
      return rejectWithValue(error);
    }
  }
);

export const timeOffBlackoutPeriodsAdapter =
  createEntityAdapter<TimeOffBlackoutPeriod>();

const initialState: TimeOffBlackoutPeriodsState = {
  ...timeOffBlackoutPeriodsAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

export const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setAll: timeOffBlackoutPeriodsAdapter.setAll,
    removeAll: timeOffBlackoutPeriodsAdapter.removeAll,
  },
  extraReducers: builder => {
    builder.addCase(
      Schedule.resourceActionEvent('fetch'),
      (state, action: ScheduleFetchAction) => {
        state.status = 'succeeded';
        if (action.payload?.included) {
          try {
            const timeOffBlackoutPeriods: TimeOffBlackoutPeriod[] = (
              action.payload.included as TimeOffBlackoutPeriodSerializedEntity[]
            )
              .filter(
                (entity: TimeOffBlackoutPeriodSerializedEntity) =>
                  entity.type === 'timeOffBlackoutPeriod'
              )
              .map((entity: TimeOffBlackoutPeriodSerializedEntity) => ({
                id: entity.id,
                type: entity.type,
                startDate: entity.attributes.startDate,
                endDate: entity.attributes.endDate,
                employeesCount: entity.attributes.employeesCount,
                location: entity.relationships?.location?.data,
              }));
            timeOffBlackoutPeriodsAdapter.setAll(state, timeOffBlackoutPeriods);
          } catch (error) {
            captureHydrationException(state, error, SLICE_NAME);
          }
        }
      }
    );
    builder
      .addCase(createTimeOffBlackout.fulfilled, (state, action) => {
        const data = action.payload;
        timeOffBlackoutPeriodsAdapter.addOne(state, {
          id: data.id,
          startDate: data.attributes.startDate,
          endDate: data.attributes.endDate,
          employeesCount: data.attributes.employeesCount,
          location: data.relationships?.location?.data,
        });
      })
      .addCase(updateTimeOffBlackout.fulfilled, (state, action) => {
        const data = action.payload;
        timeOffBlackoutPeriodsAdapter.upsertOne(state, {
          id: data.id,
          startDate: data.attributes.startDate,
          endDate: data.attributes.endDate,
          employeesCount: data.attributes.employeesCount,
          location: data.relationships?.location?.data,
        });
      })
      .addCase(deleteTimeOffBlackout.fulfilled, (state, action) => {
        timeOffBlackoutPeriodsAdapter.removeOne(state, action.payload);
      });
  },
});

const { selectById, selectAll } = timeOffBlackoutPeriodsAdapter.getSelectors(
  (state: RootState) => state.get?.(PARENT_SLICE_NAME)?.[SLICE_NAME]
);

export const selectTimeOffBlackoutPeriodById = (
  state: RootState,
  id: EntityId
) => selectById(state, id);

export const selectTimeOffBlackoutEndDate = (state: RootState, id: EntityId) =>
  selectById(state, id)?.endDate;

export const selectTimeOffBlackoutStartDate = (
  state: RootState,
  id: EntityId
) => selectById(state, id)?.startDate;

export const selectTimeOffBlackoutEmployeesCount = (
  state: RootState,
  id: string
) => selectById(state, id)?.employeesCount;

export const selectTimeOffBlackoutByDate = createSelector(
  selectAll,
  (timeOffBlackouts: TimeOffBlackoutPeriod[]) =>
    timeOffBlackouts.reduce(
      (
        memo: Record<
          string,
          {
            startDate: string;
            endDate: string;
            employeesCount: number;
            id: EntityId;
          }
        >,
        timeOffBlackout: TimeOffBlackoutPeriod
      ) => {
        const { startDate, endDate, employeesCount } = timeOffBlackout;
        datesInRange(startDate, endDate).forEach(date => {
          memo[date] = {
            startDate,
            endDate,
            employeesCount,
            id: timeOffBlackout.id,
          };
        });

        return memo;
      },
      {}
    )
);

export const selectBlackoutOnDate = createCachedSelector(
  (state: RootState, props: { date: string }) =>
    selectTimeOffBlackoutByDate(state)[props.date]?.id,
  id => id
)((_, props) => `time-off-blackout-on-date-${props.date}`);

export const selectHasBlackoutOnDateRange = createSelector(
  (state: RootState) => selectDatesInCurrentRange(state),
  selectTimeOffBlackoutByDate,
  (dates, timeOffByDate) => {
    const items = [];
    dates.forEach(date => {
      const timeOff = timeOffByDate[date];
      if (timeOff) {
        items.push(timeOff);
      }
    });
    return items.length > 0;
  }
);

export const { setAll, removeAll } = slice.actions;
export const { reducer } = slice;
